import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { hasConsented, acceptAll, refuseAll, saveConsent } from '../utils/consentManager';

const copy = {
  fr: {
    title: 'Votre exp\u00e9rience, avec transparence.',
    body: 'Nous utilisons des cookies pour am\u00e9liorer le site, mesurer la performance et, avec votre accord, activer les outils d\u2019analyse et de marketing.',
    accept: 'Accepter',
    refuse: 'Refuser',
    customize: 'Personnaliser',
    privacy: 'Politique de confidentialit\u00e9',
    save: 'Enregistrer mes choix',
    categories: {
      essential: { label: 'Essentiels', desc: 'N\u00e9cessaires au fonctionnement du site.' },
      analytics: { label: 'Analyse', desc: 'Mesure de performance et am\u00e9lioration.' },
      marketing: { label: 'Marketing', desc: 'Publicit\u00e9 personnalis\u00e9e et suivi.' },
    },
  },
  en: {
    title: 'Your experience, with transparency.',
    body: 'We use cookies to improve the site, measure performance and, with your consent, enable analytics and marketing tools.',
    accept: 'Accept',
    refuse: 'Decline',
    customize: 'Customize',
    privacy: 'Privacy policy',
    save: 'Save my preferences',
    categories: {
      essential: { label: 'Essential', desc: 'Required for the site to function.' },
      analytics: { label: 'Analytics', desc: 'Performance measurement and improvement.' },
      marketing: { label: 'Marketing', desc: 'Personalized advertising and tracking.' },
    },
  },
};

export default function CookieConsent() {
  const { language } = useLanguage();
  const t = copy[language] || copy.fr;
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

  useEffect(() => {
    if (hasConsented()) return;
    const show = () => setVisible(true);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(show, { timeout: 2000 });
    } else {
      setTimeout(show, 1500);
    }
  }, []);

  if (!visible) return null;
  if (window.location.pathname.startsWith('/admin')) return null;

  const close = () => { setVisible(false); setShowPrefs(false); };

  const handleAccept = () => { acceptAll(); close(); };
  const handleRefuse = () => { refuseAll(); close(); };
  const handleSave = () => {
    saveConsent({ essential: true, ...prefs });
    close();
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
        pointerEvents: 'none',
        animation: 'cookieSlideUp 0.5s cubic-bezier(0.16,1,0.3,1) forwards',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 620,
          background: '#111',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: showPrefs ? '28px 28px 24px' : '24px 28px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
          fontFamily: 'Inter, sans-serif',
          color: '#e5e5e5',
        }}
      >
        {/* Title */}
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em' }}>
          {t.title}
        </p>

        {/* Body */}
        <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)', fontWeight: 400 }}>
          {t.body}
        </p>

        {/* Preferences panel */}
        {showPrefs && (
          <div
            data-testid="cookie-preferences-panel"
            style={{
              marginTop: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {['essential', 'analytics', 'marketing'].map((cat) => {
              const isEssential = cat === 'essential';
              const checked = isEssential ? true : prefs[cat];
              return (
                <label
                  key={cat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.04)',
                    cursor: isEssential ? 'default' : 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => { if (!isEssential) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={(e) => { if (!isEssential) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                >
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>
                      {t.categories[cat].label}
                    </span>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginLeft: 8 }}>
                      {t.categories[cat].desc}
                    </span>
                  </div>
                  <span
                    data-testid={`cookie-toggle-${cat}`}
                    role="switch"
                    aria-checked={checked}
                    tabIndex={isEssential ? -1 : 0}
                    onClick={(e) => {
                      if (isEssential) return;
                      e.preventDefault();
                      setPrefs((p) => ({ ...p, [cat]: !p[cat] }));
                    }}
                    onKeyDown={(e) => {
                      if (isEssential) return;
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        setPrefs((p) => ({ ...p, [cat]: !p[cat] }));
                      }
                    }}
                    style={{
                      flexShrink: 0,
                      width: 38,
                      height: 22,
                      borderRadius: 11,
                      background: checked ? '#fff' : 'rgba(255,255,255,0.15)',
                      position: 'relative',
                      transition: 'background 0.25s',
                      cursor: isEssential ? 'not-allowed' : 'pointer',
                      opacity: isEssential ? 0.5 : 1,
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 3,
                        left: checked ? 19 : 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: checked ? '#111' : '#666',
                        transition: 'left 0.25s, background 0.25s',
                      }}
                    />
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {/* Buttons */}
        <div
          style={{
            marginTop: showPrefs ? 18 : 16,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {showPrefs ? (
            <button
              data-testid="cookie-save-btn"
              onClick={handleSave}
              style={{
                flex: 1,
                padding: '10px 20px',
                borderRadius: 80,
                border: 'none',
                background: '#fff',
                color: '#111',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                fontFamily: 'Inter, sans-serif',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {t.save}
            </button>
          ) : (
            <>
              <button
                data-testid="cookie-accept-btn"
                onClick={handleAccept}
                style={{
                  padding: '10px 22px',
                  borderRadius: 80,
                  border: 'none',
                  background: '#fff',
                  color: '#111',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                {t.accept}
              </button>
              <button
                data-testid="cookie-refuse-btn"
                onClick={handleRefuse}
                style={{
                  padding: '10px 22px',
                  borderRadius: 80,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, color 0.2s',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              >
                {t.refuse}
              </button>
              <button
                data-testid="cookie-customize-btn"
                onClick={() => setShowPrefs(true)}
                style={{
                  padding: '10px 22px',
                  borderRadius: 80,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, color 0.2s',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              >
                {t.customize}
              </button>
            </>
          )}

          {/* Privacy link */}
          <a
            data-testid="cookie-privacy-link"
            href="/politique-de-confidentialite"
            style={{
              marginLeft: 'auto',
              fontSize: 12,
              color: 'rgba(255,255,255,0.65)',
              textDecoration: 'none',
              transition: 'color 0.2s',
              whiteSpace: 'nowrap',
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
          >
            {t.privacy}
          </a>
        </div>
      </div>

      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
